import dayjs from 'dayjs';
import { colors } from 'theme/tokens';
import {
  KcAdminData,
  KcConfig,
  KcConfigFlag,
  KcExclusion,
  KcHomePosition,
  KcMilestoneCount,
  KcRow,
  KcScope,
  KcStep,
  KcSummary,
  KcSyncResult,
  KcTrend,
  KcTrendKey,
} from './types';

// ------------------------------------------------------------------ giới hạn cấu hình (§4.1)

/** PUT config: 1–12 mốc, mỗi mốc là số nguyên 1.000–100.000.000. */
export const MIN_MILESTONE = 1000;
export const MAX_MILESTONE = 100000000;
export const MAX_MILESTONES = 12;

/** Mặc định của máy chủ khi chưa có cấu hình (§3). */
export const DEFAULT_CONFIG: KcConfig = {
  homeEnabled: true,
  pushEnabled: true,
  homePosition: 'TOP',
  showDiamonds: true,
  showDays: true,
  showHours: true,
  showTrend: true,
  milestones: [40000, 80000, 200000, 300000, 500000, 750000, 1000000, 1600000, 2500000, 5000000],
  honorMin: 300000,
};

/**
 * Mốc bắt đầu vinh danh thực dùng — cùng luật `honorMinOf` của máy chủ: `wanted` có trong danh
 * sách thì giữ; không thì lấy mốc đầu tiên ≥ `wanted` (thiếu → 300.000), hết thì mốc cao nhất.
 */
export function honorMinOf(milestones: number[], wanted: unknown): number {
  const target = Number(wanted);
  if (milestones.indexOf(target) >= 0) return target;
  const goal = Number.isFinite(target) && target > 0 ? target : DEFAULT_CONFIG.honorMin;
  const found = milestones.find((value) => value >= goal);
  return found ?? milestones[milestones.length - 1] ?? DEFAULT_CONFIG.honorMin;
}

/** Mọi công tắc của cấu hình (thiếu trường → true như máy chủ). */
export const CONFIG_FLAGS: KcConfigFlag[] = [
  'homeEnabled',
  'pushEnabled',
  'showDiamonds',
  'showDays',
  'showHours',
  'showTrend',
];

/** Công tắc "Hiện trong phân tích trên app" (chỉ người xem nội bộ thấy câu phân tích). */
export const ANALYSIS_FLAG_OPTIONS: { key: KcConfigFlag; label: string }[] = [
  { key: 'showDiamonds', label: 'Số Kim cương' },
  { key: 'showDays', label: 'Số ngày LIVE' },
  { key: 'showHours', label: 'Số giờ LIVE' },
  { key: 'showTrend', label: 'Xu hướng bậc (Tăng / Duy trì / Giảm)' },
];

/** Vị trí dải chữ chạy trên Trang chủ app, đúng thứ tự các khối từ trên xuống. */
export const HOME_POSITION_OPTIONS: { value: KcHomePosition; label: string }[] = [
  { value: 'TOP', label: 'Đầu trang (trên thẻ sự kiện)' },
  { value: 'AFTER_HERO', label: 'Dưới thẻ sự kiện' },
  { value: 'AFTER_APPS', label: 'Dưới lối tắt & ứng dụng' },
  { value: 'AFTER_LIVE_NOW', label: 'Dưới Đang diễn ra' },
  { value: 'AFTER_CREATORS', label: 'Dưới danh sách Creator' },
  { value: 'AFTER_NEWS', label: 'Dưới Tin tức' },
  { value: 'AFTER_PARTNERS', label: 'Dưới Đối tác (cuối trang)' },
];

export const HOME_POSITION_LABEL = HOME_POSITION_OPTIONS.reduce(
  (map, option) => ({ ...map, [option.value]: option.label }),
  {} as Record<KcHomePosition, string>
);

/** Giá trị lạ / thiếu → 'TOP' (mặc định của máy chủ). */
export function toHomePosition(value: unknown): KcHomePosition {
  return HOME_POSITION_OPTIONS.some((option) => option.value === value)
    ? (value as KcHomePosition)
    : DEFAULT_CONFIG.homePosition;
}

// ------------------------------------------------------------------ số, ngày

const numberFormat = new Intl.NumberFormat('vi-VN');

/** 1234567 → "1.234.567"; trống / không phải số → "—". */
export function formatNumber(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(num) ? numberFormat.format(num) : '—';
}

/**
 * Nhãn mốc — cùng kiểu máy chủ (src/kc-milestones/kc-milestones.rules.ts):
 * 300K, 750K, 1M, 1M6, 2M5, 5M (chủ dự án chọn 01/10: 2,5 triệu = 2M5).
 */
export function milestoneLabel(value?: number | null): string {
  if (!value || !Number.isFinite(value)) return '—';
  const unit = (hundredths: number, suffix: string) => {
    const rest = String(hundredths % 100).padStart(2, '0').replace(/0+$/, '');
    return `${Math.floor(hundredths / 100)}${suffix}${rest}`;
  };
  if (value >= 1000000) return unit(Math.round(value / 10000), 'M');
  if (value >= 1000) return unit(Math.round(value / 10), 'K');
  return String(value);
}

/** Tháng hiện tại theo UTC ("YYYY-MM"), cùng cách máy chủ chọn tháng mặc định. */
export function currentMonthUtc(): string {
  return new Date().toISOString().slice(0, 7);
}

/**
 * 'YYYY-MM-DD' (ngày TikTok, UTC+0) → 'DD/MM/YYYY'. Cắt chuỗi chứ không qua Date để máy ở
 * giờ Việt Nam không bị lệch một ngày.
 */
export function formatDay(value?: string | null): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
}

/** 'YYYY-MM-DD' → 'DD/MM'. */
export function formatDayMonth(value?: string | null): string {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(value || '');
  return match ? `${match[2]}/${match[1]}` : '—';
}

/** 'YYYY-MM' → 'MM/YYYY'. */
export function formatMonth(value?: string | null): string {
  const match = /^(\d{4})-(\d{2})/.exec(value || '');
  return match ? `${match[2]}/${match[1]}` : '—';
}

/** Mốc thời gian ISO (UTC) → giờ máy người xem "DD/MM HH:mm". */
export function formatDateTimeShort(value?: string | null): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM HH:mm') : '—';
}

/** TikTok ID hiển thị có đúng một dấu @ ở đầu. */
export function atName(name?: string | null): string {
  const clean = (name || '').trim().replace(/^@+/, '');
  return clean ? `@${clean}` : '';
}

/**
 * Tên hiển thị của một Creator: "@tiktokid"; máy chủ không biết TikTok ID thì gửi "UID 7…" (hoặc
 * null ở danh sách loại trừ) → hiện "UID 7…", không thêm @.
 */
export function creatorName(username: string | null | undefined, tiktokUid: string): string {
  const clean = (username || '').trim();
  if (!clean || /^UID\s/i.test(clean)) return `UID ${tiktokUid}`;
  return atName(clean);
}

/** Mốc thời gian ISO → ngày theo giờ máy người xem "DD/MM/YYYY". */
export function formatDateLocal(value?: string | null): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—';
}

// ------------------------------------------------------------------ xu hướng

export interface TrendMeta {
  label: string;
  /** Câu ngắn dưới con số ở ô tóm tắt. */
  hint: string;
  /** Màu chữ câu phân tích / chấm màu ở ô tóm tắt. */
  ink: string;
  soft: string;
}

export const TREND_META: Record<KcTrendKey, TrendMeta> = {
  UP: {
    label: 'Tăng',
    hint: 'Lên khung mốc so với tháng trước',
    ink: colors.successInk,
    soft: colors.successSoft,
  },
  KEEP: {
    label: 'Duy trì',
    hint: 'Giữ khung mốc của tháng trước',
    ink: colors.infoInk,
    soft: colors.infoSoft,
  },
  BELOW: {
    label: 'Chưa giữ mốc',
    hint: 'Chưa đủ KC để giữ mốc tháng trước',
    ink: colors.warningInk,
    soft: colors.warningSoft,
  },
  DOWN: {
    label: 'Giảm',
    hint: 'Xuống khung mốc so với tháng trước',
    ink: colors.dangerInk,
    soft: colors.dangerSoft,
  },
  NONE: {
    label: 'Chưa rõ',
    hint: 'Không có số tháng trước để so',
    ink: colors.textSecondary,
    soft: colors.surfaceMuted,
  },
};

/**
 * Ô xu hướng cần hiện: tháng đang chạy chưa nói "Giảm" (chỉ "Chưa giữ mốc"); tháng đã khép thì
 * ngược lại (§2, Q1).
 */
export function trendKeysFor(closed: boolean): KcTrendKey[] {
  return closed ? ['UP', 'KEEP', 'DOWN', 'NONE'] : ['UP', 'KEEP', 'BELOW', 'NONE'];
}

export function trendInk(trend?: KcTrend | null): string {
  return trend ? TREND_META[trend].ink : TREND_META.NONE.ink;
}

// ------------------------------------------------------------------ màu mốc

// Từ mốc thấp tới mốc cao; mốc cao nhất luôn là màu vàng.
const MILESTONE_PALETTE = ['cyan', 'blue', 'geekblue', 'purple', 'magenta', 'volcano', 'orange', 'gold'];

/** Mọi mốc đang có trên trang (cấu hình + sự kiện cũ theo mốc đã bỏ), tăng dần, không trùng. */
export function milestoneOrderOf(data: KcAdminData | null, config: KcConfig | null): number[] {
  const values: number[] = [];
  const push = (value: number) => {
    if (Number.isFinite(value) && value > 0 && values.indexOf(value) < 0) values.push(value);
  };
  (config?.milestones || []).forEach(push);
  (data?.summary.byMilestone || []).forEach((item) => push(item.milestone));
  (data?.rows || []).forEach((row) => {
    push(row.milestone);
    row.steps.forEach((step) => push(step.milestone));
  });
  return values.sort((a, b) => a - b);
}

/** Màu thẻ antd của một mốc theo thứ hạng của nó trong `order`. */
export function milestoneColor(milestone: number, order: number[]): string {
  const rank = order.indexOf(milestone);
  const size = MILESTONE_PALETTE.length;
  if (rank < 0 || order.length === 0) return 'default';
  if (order.length <= size) return MILESTONE_PALETTE[size - order.length + rank];
  return MILESTONE_PALETTE[Math.round((rank * (size - 1)) / (order.length - 1))];
}

// ------------------------------------------------------------------ bảng

/** Mặc định: mốc cao → thấp, rồi KC tháng nhiều → ít, rồi theo TikTok ID. */
export function sortRows(rows: KcRow[]): KcRow[] {
  return rows
    .slice()
    .sort(
      (a, b) =>
        b.milestone - a.milestone ||
        b.diamonds - a.diamonds ||
        a.username.localeCompare(b.username)
    );
}

/** Lọc tại máy theo TikTok ID / tên nhóm (bỏ dấu @, không phân biệt hoa thường). */
export function filterRows(rows: KcRow[], keyword: string): KcRow[] {
  const needle = keyword.trim().replace(/^@+/, '').toLowerCase();
  if (!needle) return rows;
  return rows.filter(
    (row) =>
      row.username.toLowerCase().includes(needle) ||
      (row.groupName || '').toLowerCase().includes(needle) ||
      row.tiktokUid.includes(needle)
  );
}

/** Ẩn / hiện một Creator xong: sửa dòng đó và hai số Hiện / Ẩn của phần tóm tắt. */
export function applyHidden(
  data: KcAdminData,
  tiktokUid: string,
  hidden: boolean,
  byName: string | null
): KcAdminData {
  const target = data.rows.find((row) => row.tiktokUid === tiktokUid);
  if (!target || target.hidden === hidden) return data;
  // Dòng đã loại trừ không nằm trong hai số Hiện / Ẩn (máy chủ đếm riêng ở summary.excluded).
  const delta = target.excluded ? 0 : hidden ? 1 : -1;
  return {
    ...data,
    summary: {
      ...data.summary,
      hidden: Math.max(0, data.summary.hidden + delta),
      visible: Math.max(0, data.summary.visible - delta),
    },
    rows: data.rows.map((row) =>
      row.tiktokUid === tiktokUid
        ? {
            ...row,
            hidden,
            hiddenAt: hidden ? new Date().toISOString() : null,
            hiddenByName: hidden ? byName : null,
          }
        : row
    ),
  };
}

// ------------------------------------------------------------------ cấu hình (ô sửa mốc)

/** Lỗi của từng ô mốc (null = hợp lệ), cùng thứ tự với danh sách đang sửa. */
export function milestoneInputErrors(values: (number | null)[]): (string | null)[] {
  return values.map((value, index) => {
    if (value === null || value === undefined) return 'Nhập số Kim cương.';
    if (!Number.isInteger(value) || value < MIN_MILESTONE || value > MAX_MILESTONE) {
      return `Mốc phải là số nguyên từ ${formatNumber(MIN_MILESTONE)} đến ${formatNumber(
        MAX_MILESTONE
      )}.`;
    }
    if (values.indexOf(value) !== index) return 'Trùng với một mốc khác.';
    return null;
  });
}

/** Lỗi chung của cả danh sách (số lượng mốc); null = hợp lệ. */
export function milestoneListError(values: (number | null)[]): string | null {
  if (values.length < 1) return 'Cần ít nhất 1 mốc.';
  if (values.length > MAX_MILESTONES) return `Tối đa ${MAX_MILESTONES} mốc.`;
  return null;
}

/** Danh sách hợp lệ → tăng dần (máy chủ cũng tự sắp như vậy). */
export function sortedMilestones(values: (number | null)[]): number[] {
  return values
    .filter((value): value is number => typeof value === 'number')
    .slice()
    .sort((a, b) => a - b);
}

export function sameMilestones(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

// ------------------------------------------------------------------ đọc phản hồi máy chủ

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : toNumber(value, 0);

const toText = (value: unknown): string => (typeof value === 'string' ? value : '');

const toNullableText = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

const TRENDS: KcTrend[] = ['UP', 'KEEP', 'BELOW', 'DOWN'];

/** Điền mặc định cho cấu hình để giao diện không vỡ khi thiếu trường. */
export function normalizeConfig(raw: any): KcConfig {
  const list = Array.isArray(raw?.milestones)
    ? raw.milestones.map((value: unknown) => toNumber(value)).filter((value: number) => value > 0)
    : [];
  const flag = (key: KcConfigFlag) =>
    typeof raw?.[key] === 'boolean' ? (raw[key] as boolean) : DEFAULT_CONFIG[key];
  const milestones = list.length ? sortedMilestones(list) : DEFAULT_CONFIG.milestones.slice();
  return {
    homeEnabled: flag('homeEnabled'),
    pushEnabled: flag('pushEnabled'),
    homePosition: toHomePosition(raw?.homePosition),
    showDiamonds: flag('showDiamonds'),
    showDays: flag('showDays'),
    showHours: flag('showHours'),
    showTrend: flag('showTrend'),
    milestones,
    honorMin: honorMinOf(milestones, raw?.honorMin ?? DEFAULT_CONFIG.honorMin),
  };
}

function normalizeStep(raw: any): KcStep {
  const milestone = toNumber(raw?.milestone);
  return {
    milestone,
    label: toText(raw?.label) || milestoneLabel(milestone),
    dataDate: toText(raw?.dataDate),
    dropped: raw?.dropped === true,
  };
}

function normalizeRow(raw: any): KcRow {
  const milestone = toNumber(raw?.milestone);
  const analysis = raw?.analysis;
  const trend = TRENDS.indexOf(analysis?.trend) >= 0 ? (analysis.trend as KcTrend) : null;
  return {
    tiktokUid: String(raw?.tiktokUid ?? ''),
    username: toText(raw?.username).replace(/^@+/, '') || String(raw?.tiktokUid ?? ''),
    groupName: toNullableText(raw?.groupName),
    userId: raw?.userId === null || raw?.userId === undefined ? null : toNumber(raw.userId),
    milestone,
    label: toText(raw?.label) || milestoneLabel(milestone),
    reachedDate: toText(raw?.reachedDate),
    steps: (Array.isArray(raw?.steps) ? raw.steps.map(normalizeStep) : []).sort(
      (a: KcStep, b: KcStep) => a.milestone - b.milestone
    ),
    diamonds: toNumber(raw?.diamonds),
    hidden: raw?.hidden === true,
    hiddenAt: toNullableText(raw?.hiddenAt),
    hiddenByName: toNullableText(raw?.hiddenByName),
    pushedAt: toNullableText(raw?.pushedAt),
    // Máy chủ cũ không gửi: mọi dòng đều là Creator được vinh danh, chưa ai bị loại trừ.
    honored: raw?.honored !== false,
    excluded: raw?.excluded === true,
    analysis:
      analysis && typeof analysis === 'object'
        ? {
            diamonds: toNumber(analysis.diamonds),
            liveDays: toNumber(analysis.liveDays),
            liveHours: toNumber(analysis.liveHours),
            prevDiamonds: toNullableNumber(analysis.prevDiamonds),
            prevLiveDays: toNullableNumber(analysis.prevLiveDays),
            prevLiveHours: toNullableNumber(analysis.prevLiveHours),
            prevMilestone: toNullableNumber(analysis.prevMilestone),
            trend,
            toKeep: toNullableNumber(analysis.toKeep),
            text: toText(analysis.text),
          }
        : null,
  };
}

function normalizeSummary(raw: any, rows: KcRow[]): KcSummary {
  const excludedCount = rows.filter((row) => row.excluded).length;
  const hiddenCount = rows.filter((row) => row.hidden && !row.excluded).length;
  const byMilestone: KcMilestoneCount[] = Array.isArray(raw?.byMilestone)
    ? raw.byMilestone.map((item: any) => {
        const milestone = toNumber(item?.milestone);
        return {
          milestone,
          label: toText(item?.label) || milestoneLabel(milestone),
          count: toNumber(item?.count),
        };
      })
    : [];
  const trend = raw?.trend || {};
  return {
    creators: toNumber(raw?.creators, rows.length),
    visible: toNumber(raw?.visible, rows.length - hiddenCount - excludedCount),
    hidden: toNumber(raw?.hidden, hiddenCount),
    excluded: toNumber(raw?.excluded, excludedCount),
    byMilestone,
    trend: {
      UP: toNumber(trend.UP),
      KEEP: toNumber(trend.KEEP),
      BELOW: toNumber(trend.BELOW),
      DOWN: toNumber(trend.DOWN),
      NONE: toNumber(trend.NONE),
    },
  };
}

/** GET /kc-milestones/admin → dữ liệu trang (thiếu trường → mặc định). */
export function normalizeAdminData(raw: any, requestedMonth: string): KcAdminData {
  const rows: KcRow[] = Array.isArray(raw?.rows) ? raw.rows.map(normalizeRow) : [];
  const month = typeof raw?.month === 'string' && /^\d{4}-\d{2}$/.test(raw.month) ? raw.month : requestedMonth;
  return {
    month,
    closed: typeof raw?.closed === 'boolean' ? raw.closed : month < currentMonthUtc(),
    asOf: toNullableText(raw?.asOf),
    config: normalizeConfig(raw?.config),
    scope: raw?.scope === 'all' ? 'all' : 'honor',
    counts: {
      honor: toNumber(raw?.counts?.honor, rows.filter((row) => row.honored).length),
      below: toNumber(raw?.counts?.below, rows.filter((row) => !row.honored).length),
    },
    summary: normalizeSummary(raw?.summary, rows),
    rows: sortRows(rows),
  };
}

/** Tham số GET admin: không có tháng → máy chủ tự chọn; `scope` chỉ gửi khi xem cả dưới mốc. */
export function adminQuery(month: string, scope: KcScope): { month?: string; scope?: 'all' } {
  return {
    ...(month ? { month } : {}),
    ...(scope === 'all' ? { scope: 'all' as const } : {}),
  };
}

/** GET /kc-milestones/admin/exclusions → danh sách (mới thêm trước, như máy chủ trả). */
export function normalizeExclusions(raw: any): KcExclusion[] {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
  return list
    .map((item: any) => ({
      tiktokUid: String(item?.tiktokUid ?? '').trim(),
      username: toNullableText(item?.username),
      note: toNullableText(item?.note),
      createdAt: toNullableText(item?.createdAt),
      createdByName: toNullableText(item?.createdByName),
    }))
    .filter((item: KcExclusion) => !!item.tiktokUid);
}

export function normalizeSyncResult(raw: any, month: string): KcSyncResult {
  return {
    month: toText(raw?.month) || month,
    created: toNumber(raw?.created),
    dropped: toNumber(raw?.dropped),
    restored: toNumber(raw?.restored),
    pushed: toNumber(raw?.pushed),
  };
}

export function syncResultText(result: KcSyncResult): string {
  return `Đã tính lại: +${formatNumber(result.created)} mốc mới, ${formatNumber(
    result.dropped
  )} tụt mốc, ${formatNumber(result.restored)} khôi phục`;
}

// ------------------------------------------------------------------ lỗi

function rawMessage(err: any): string {
  if (Array.isArray(err?.message)) return err.message.join(', ');
  if (typeof err?.message === 'string') return err.message;
  if (typeof err?.error === 'string') return err.error;
  return '';
}

/**
 * Interceptor axios ném `error.response.data` = { statusCode, message, error? }. Máy chủ báo lỗi
 * tháng / mốc bằng 400 { message } tiếng Việt → hiện nguyên câu đó.
 */
export function kcErrorMessage(err: any): string {
  const status: number | undefined = err?.statusCode || err?.status;
  const raw = rawMessage(err);
  if (status === 404 && /^Cannot (GET|POST|PUT|PATCH|DELETE) /i.test(raw)) {
    return 'Máy chủ chưa có chức năng Chúc mừng đạt mốc. Hãy báo bộ phận kỹ thuật cập nhật máy chủ rồi tải lại trang.';
  }
  if (status === 429 || /ThrottlerException|Too Many Requests/i.test(raw)) {
    return 'Bạn thao tác quá nhanh. Hãy đợi khoảng 1 phút rồi thử lại.';
  }
  if (raw === 'Forbidden' || raw === 'Unauthorized' || status === 401 || status === 403) {
    return 'Bạn không có quyền thực hiện thao tác này.';
  }
  if (/^Internal server error$/i.test(raw) || (status !== undefined && status >= 500)) {
    return 'Lỗi máy chủ. Vui lòng thử lại sau.';
  }
  return raw || 'Có lỗi xảy ra. Vui lòng thử lại.';
}
